import { Box } from '@mui/material';
import { PageContainer } from '@/common/presentation/components/layout/PageContainer';
import { PageHeader } from '@/common/presentation/components/data-display/PageHeader';
import EmptyState from '@/common/presentation/components/EmptyState';
import { RwAlert, RwButton } from '@/common/presentation/components/rw';
import { LibraryGallery } from '@/common/presentation/components/library';
import { useFocusPageHeading } from '@/common/presentation/hooks/useFocusPageHeading';
import { useBlocks } from '@/blocks/application/hooks/useBlocks';
import { useInfiniteBlocks } from '@/blocks/application/hooks/useInfiniteBlocks';
import { buildKindFilterOptions } from '@/blocks/domain/helpers/buildKindFilterOptions';
import { useBlockLibraryUrlFilters } from '@/blocks/presentation/hooks/useBlockLibraryUrlFilters';
import { useBlockLibraryPageController } from '@/blocks/presentation/hooks/useBlockLibraryPageController';
import { BlockLibraryFilters } from '@/blocks/presentation/components/BlockLibraryFilters';
import { BlockLibraryCard } from '@/blocks/presentation/components/BlockLibraryCard';
import { NewBlockDialog } from '@/blocks/presentation/components/NewBlockDialog';
import { ArchiveBlockDialog } from '@/blocks/presentation/components/ArchiveBlockDialog';
import { UnarchiveBlockDialog } from '@/blocks/presentation/components/UnarchiveBlockDialog';
import { useHostConfig } from '@/host-config/presentation/HostConfigProvider';

/**
 * Size of the stable query the kind filter's options are derived from. Same page size the editors
 * already use to list blocks (`AddBlockTab`); a library bigger than this only loses options for kinds
 * that occur solely past this many blocks (the API has no distinct-kinds facet).
 */
const KIND_SCOPE_PAGE_SIZE = 100;

/**
 * The Block Library (`/blocks`, Figma `bl.library.default` node `6:3`, LLAMS-617): NextGen
 * gallery driven entirely by `useBlockLibraryUrlFilters` (nuqs — the single source of truth, no
 * local mirror) plus `useInfiniteBlocks`' real server paging ("Load more" appends the next page).
 * Kept a thin composition of hooks + `LibraryGallery`; archive/duplicate/restore/snackbar logic lives
 * in `useBlockLibraryPageController`.
 */
export default function BlockLibraryPage() {
  const cfg = useHostConfig();
  const [filters] = useBlockLibraryUrlFilters();
  const controller = useBlockLibraryPageController();
  useFocusPageHeading(controller.focusHeadingToken);

  const { data, isLoading, isError, isFetchNextPageError, isFetching, isFetchingNextPage, fetchNextPage, refetch } = useInfiniteBlocks({
    search: filters.q,
    kind: filters.kind === 'all' ? undefined : filters.kind,
    statusFilter: filters.status
  });

  // QA-DB09-3: the kind filter lists the kinds present in the results for the current search + status,
  // ignoring the kind filter itself (else picking one kind would collapse the list to that kind) AND the
  // "Load more" window (else a kind further down the list would not be offered). The query key therefore
  // depends on search + status only, so it is cached across kind changes and Load more.
  const { data: kindScopeData } = useBlocks({
    page: 1,
    pageSize: KIND_SCOPE_PAGE_SIZE,
    search: filters.q,
    statusFilter: filters.status
  });
  const kindOptions = buildKindFilterOptions(kindScopeData?.items ?? [], filters.kind);

  const items = data?.items ?? [];
  const hasActiveFilters = Boolean(filters.q.trim()) || filters.kind !== 'all' || filters.status !== 'all-active';
  return (
    <PageContainer variant="list" sx={{ p: 'var(--size-16)' }} data-testid="document-builder:block-library:root">
      <PageHeader
        title={cfg.blockLibraryPage.title}
        description={cfg.blockLibraryPage.subtitle}
        actionLabel={cfg.blockLibraryPage.newButtonLabel}
        onAction={() => controller.setDialogOpen(true)}
        actionTestId="document-builder:block-library:new"
      />

      {controller.snackbarMessage ? (
        <RwAlert
          tone={controller.snackbarSeverity === 'success' ? 'success' : 'critical'}
          title={controller.snackbarMessage}
          onDismiss={() => controller.setSnackbarMessage(null)}
          sx={{ mb: 'var(--size-16)' }}
          data-testid="document-builder:block-library:snackbar"
        />
      ) : null}

      <BlockLibraryFilters kindOptions={kindOptions} />

      <LibraryGallery
        ariaLabel="Block library"
        testIdPrefix="document-builder:block-library"
        noun="blocks"
        singularNoun="block"
        itemCount={items.length}
        totalCount={data?.totalCount ?? items.length}
        isLoading={isLoading}
        isLoadingMore={isFetchingNextPage}
        // The grid keeps the previous result while a new filter / retry is in flight: say so.
        isRefreshing={isFetching && !isLoading && !isFetchingNextPage}
        // A failed "Load more" keeps the grid and shows an inline footer alert; the full-page error is only for
        // a list with nothing to show (first page / new filter failed).
        error={isError && !isFetchNextPageError ? { message: 'Failed to load blocks.', onRetry: () => void refetch() } : null}
        loadMoreError={isFetchNextPageError ? 'Failed to load more blocks.' : null}
        emptyState={
          hasActiveFilters ? (
            <EmptyState title="No blocks match your filters" description="Try adjusting search, kind, or status filters." />
          ) : (
            // BL-08: an empty library is not a filter miss — say so, and put the way forward right there.
            <EmptyState
              title="No blocks yet"
              description="Create a block to get started."
              action={
                <RwButton
                  tier="primary"
                  onClick={() => controller.setDialogOpen(true)}
                  data-testid="document-builder:block-library:empty-new"
                >
                  {cfg.blockLibraryPage.newButtonLabel}
                </RwButton>
              }
            />
          )
        }
        onLoadMore={fetchNextPage}
      >
        {items.map((block) => (
          <Box component="li" key={block.blockId} sx={{ display: 'flex' }}>
            <BlockLibraryCard
              block={block}
              onOpen={() => controller.openBlock(block.blockId)}
              onDuplicate={() => controller.handleDuplicate(block)}
              isDuplicateLoading={controller.duplicatePendingBlockId === block.blockId}
              onArchive={block.status !== 'Archived' ? () => controller.openArchiveDialog(block) : undefined}
              isArchiveLoading={controller.isArchivePending && controller.archiveTarget?.blockId === block.blockId}
              onRestore={block.status === 'Archived' ? () => controller.setRestoreTarget(block) : undefined}
              isRestoreLoading={controller.isRestorePending && controller.restoreTarget?.blockId === block.blockId}
            />
          </Box>
        ))}
      </LibraryGallery>

      <NewBlockDialog open={controller.dialogOpen} onClose={() => controller.setDialogOpen(false)} />

      <ArchiveBlockDialog
        open={Boolean(controller.archiveTarget)}
        blockName={controller.archiveTarget?.name ?? ''}
        isArchiving={controller.isArchivePending}
        error={controller.archiveError}
        onClose={controller.closeArchiveDialog}
        onConfirm={() => {
          void controller.handleConfirmArchive();
        }}
      />

      <UnarchiveBlockDialog
        open={Boolean(controller.restoreTarget)}
        blockName={controller.restoreTarget?.name ?? ''}
        isRestoring={controller.isRestorePending}
        onClose={() => {
          if (!controller.isRestorePending) controller.setRestoreTarget(null);
        }}
        onConfirm={() => {
          void controller.handleConfirmRestore();
        }}
      />
    </PageContainer>
  );
}
